/* eslint-disable react-hooks/exhaustive-deps */
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { Tooltip } from "@material-tailwind/react";
import {
  ActiveInactiveUsers,
  CumulativeChurn,
  CumulativeUsersOverTime,
  NonCumulativeUsersOverTime,
  UserFilters,
  RegidsteredUsersChart,
} from "../../components";
import { useEffect, useState } from "react";
import { useUserStats } from "../../store/useUserStats";
import { useUsers } from "../../store/useUsers";
import dayjs from "dayjs";
import { useDropLocations } from "../../store/useDropLocations";
import { useMarketPricing } from "../../store/useMarketPricing";

function NewUserStatsScreen() {
  /** Hooks */
  const { getUserStats } = useUserStats();
  const { userPlatformStats, getUserPlatformStats } = useUsers();
  const { getDropLocations } = useDropLocations();
  const { getAllMarketPrices } = useMarketPricing();
  /** State */
  const [period, setPeriod] = useState<
    "all" | "yearly" | "quarterly" | "monthly" | "weekly"
  >("all");
  const [year, setYear] = useState<number>(dayjs().year());
  const [quarter, setQuarter] = useState<"Q1" | "Q2" | "Q3" | "Q4">("Q1");
  const [month, setMonth] = useState<number>(dayjs().month());
  const [week, setWeek] = useState<Date>(new Date());
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);

  /** Effects */
  useEffect(() => {
    getDropLocations();
    getAllMarketPrices();
    getUserPlatformStats();
  }, []);

  useEffect(() => {
    if (period === "all")
      getUserStats({ period, selectedFilters, selectedStores });
    if (period === "yearly")
      getUserStats({ period, year, selectedFilters, selectedStores });
    if (period === "quarterly")
      getUserStats({ period, year, quarter, selectedFilters, selectedStores });
    if (period === "monthly")
      getUserStats({ period, year, month, selectedFilters, selectedStores });
    if (period === "weekly")
      getUserStats({
        period,
        year,
        month,
        week,
        selectedFilters,
        selectedStores,
      });
  }, [period, year, quarter, month, week, selectedFilters, selectedStores]);

  return (
    <div>
      <div className="flex w-full justify-between">
        <h1 className="font-satoshi text-2xl font-bold text-dark mb-11">
          User Stats
        </h1>
        <div className="flex" />
      </div>

      <div className="flex flex-col w-full p-2 sm:p-7 bg-blue-200 border border-gray-border drop-shadow-md mb-1 sm:mb-2 mt-4">
        <p
          className={`font-satoshi font-bold text-lg sm:text-2xl text-dark mb-6`}
        >
          Definitions
        </p>
        <p className={`font-satoshi text-base text-dark mb-1`}>
          - <b>Dropper</b> - Users that have made at least 1 drop
        </p>
        <p className={`font-satoshi text-base text-dark mb-1`}>
          - <b>Regular Dropper</b> - Users that have made at least 2 drops
        </p>
        <p className={`font-satoshi text-base text-dark mb-1`}>
          - <b>Active Dropper</b> - Users that have made at least 2 drops, and
          have made a drop in the last 3 months
        </p>
        <p className={`font-satoshi text-base text-dark mb-1`}>
          - <b>Inactive Dropper</b> - Users that have made at least 2 drops, but
          have not made a drop in the last 3 months
        </p>
      </div>
      {/* Filters */}
      <UserFilters
        period={period}
        year={year}
        quarter={quarter}
        month={month}
        week={week}
        selectedFilters={selectedFilters}
        selectedStores={selectedStores}
        setPeriod={setPeriod}
        setYear={setYear}
        setQuarter={setQuarter}
        setMonth={setMonth}
        setWeek={setWeek}
        setSelectedFilters={setSelectedFilters}
        setSelectedStores={setSelectedStores}
      />
      <CumulativeUsersOverTime
        periodProp={period}
        yearProp={year}
        quarterProp={quarter}
        monthProp={month}
        weekProp={week}
        selectedFiltersProp={selectedFilters}
        selectedStoresProp={selectedStores}
      />
      <NonCumulativeUsersOverTime
        periodProp={period}
        yearProp={year}
        quarterProp={quarter}
        monthProp={month}
        weekProp={week}
        selectedFiltersProp={selectedFilters}
        selectedStoresProp={selectedStores}
      />
      <ActiveInactiveUsers
        periodProp={period}
        yearProp={year}
        quarterProp={quarter}
        monthProp={month}
        weekProp={week}
        selectedFiltersProp={selectedFilters}
        selectedStoresProp={selectedStores}
      />
      <CumulativeChurn
        periodProp={period}
        yearProp={year}
        quarterProp={quarter}
        monthProp={month}
        weekProp={week}
        selectedFiltersProp={selectedFilters}
        selectedStoresProp={selectedStores}
      />

      {/* Top Stats */}
      <div className="flex flex-col sm:flex-row w-full p-2 sm:p-7 bg-white border border-gray-border drop-shadow-md sm:h-28 mb-1 sm:mb-10">
        <Tooltip content="Users that are using iOS devices">
          <div className="flex flex-1 sm:flex-col justify-between items-center  border-b sm:border-b-0 sm:border-r border-gray-border h-full py-2 sm:py-0">
            <div className="flex items-center mb-1">
              <img
                className="w-[20px] h-[20px] mr-3"
                alt="ios-logo"
                src={require("../../assets/icons/platforms/apple.png")}
              />
              <p className="font-satoshi text-2xl text-dark font-bold">
                {userPlatformStats?.iosUsers}
              </p>
            </div>
            <p className="font-satoshi font-medium text-sm text-gray-text ml-2 sm:ml-0">
              iOS users
            </p>
          </div>
        </Tooltip>
        <Tooltip content="Users that are using Android devices">
          <div className="flex flex-1 sm:flex-col justify-between items-center  border-b sm:border-b-0 sm:border-r border-gray-border h-full py-2 sm:py-0">
            <div className="flex items-center mb-1">
              <img
                className="w-[20px] h-[20px] mr-3"
                alt="ios-logo"
                src={require("../../assets/icons/platforms/android.png")}
              />
              <p className="font-satoshi text-2xl text-dark font-bold">
                {userPlatformStats?.androidUsers}
              </p>
            </div>
            <p className="font-satoshi font-medium text-sm text-gray-text ml-2 sm:ml-0">
              Android users
            </p>
          </div>
        </Tooltip>
        <Tooltip content="User that doesn't have latest version, or they did not login yet!">
          <div className="flex flex-1 sm:flex-col justify-between items-center  border-b sm:border-b-0 sm:border-r border-gray-border h-full py-2 sm:py-0">
            <div className="flex items-center mb-1">
              <p className="font-satoshi text-2xl text-dark font-bold">
                {userPlatformStats?.unknownUsers}
              </p>
            </div>
            <p className="font-satoshi font-medium text-sm text-gray-text ml-2 sm:ml-0">
              Unknown platform (not logged)
            </p>
          </div>
        </Tooltip>
      </div>
      <RegidsteredUsersChart />
    </div>
  );
}

export default withProtectedRoute(NewUserStatsScreen);
